import React, { useState, useMemo } from 'react';
import PropTypes, { any } from 'prop-types';

import SetOrganizerRatingForm from './SetOrganizerRatingForm';
import PbrRating from '../../components/text/PbrRating';

import { getRatingDetails, setRatingChoice, getRatingProgressDisplay, getPickleBallRating } from '../../utils';
import { getOrganizerRating, isPickleBallSportTypeEvent, isTennisSportTypeEvent } from '../../utils/events';
import { MatchFormatTypes } from '../../utils/postScoreConstants';
import { Utr } from '../../components/text';
import { RatingStatuses } from '../../utils/constants';
import { sportTypeIds } from '../../utils/eventConstants';

import styles from './drawEdit.scss';

const PlayerRatingV2 = (props) => {
    const {
        player,
        utrType,
        ratingType,
        setOrganizerRating,
        organizerRatings,
        showEdit,
        showOrganizerRatings,
        userIsPowered,
        sportTypeId,
        'data-testid': dataTestId,
    } = props;

    const [editRating, setEditRating] = useState(false);

    const rating = useMemo(() => {
        if (isPickleBallSportTypeEvent(sportTypeId)) {
            return getPickleBallRating(player, ratingType, true);
        }
        return getRatingDetails(setRatingChoice({ player, ratingType, utrType }), userIsPowered, true, false, false);
    }, [player, sportTypeId, ratingType, utrType, userIsPowered]);

    const organizerRating = getOrganizerRating(player.memberId, organizerRatings);
    const memberOrganizerRating = ratingType === MatchFormatTypes.SINGLES ? organizerRating?.singlesOrganizerRating : organizerRating?.doublesOrganizerRating;

    const clearAll = () => {
        setEditRating(false);
    };

    const getReliability = (playerRating) => {
        const reliabilityDisplay = getRatingProgressDisplay(playerRating.progress);
        return `(${reliabilityDisplay}%)`;
    };

    const isRatingOutsideProjectedRange = (playerRating, projectedRating) => {
        return playerRating > parseFloat(projectedRating) + 2 || rating < parseFloat(projectedRating) - 2;
    };

    const showEditRating = (playerRating) => {
        const unRatedStrings = [RatingStatuses.PROJECTED, RatingStatuses.UNRATED, RatingStatuses.ESTIMATED];
        return showEdit && unRatedStrings.includes(playerRating.status);
    };

    const submitOrganizerRating = async (values) => {
        let showPopup = false;
        if (isPickleBallSportTypeEvent(sportTypeId)) {
            showPopup = isRatingOutsideProjectedRange(values.rating, rating.sortRating);
        } else {
            showPopup = isRatingOutsideProjectedRange(values.rating, rating.utrRange ? rating.sortRating : rating.value);
        }
        await setOrganizerRating(player.memberId, ratingType, values.rating, showPopup);
        setEditRating(false);
    };

    const hasOrganizerRating = showOrganizerRatings && memberOrganizerRating;

    return (
        <div className="col-md-2 d-flex aic jcc" data-testid={dataTestId}>
            <div className="player-details">
                <div className="name text-dark">
                    {editRating ? (
                        <SetOrganizerRatingForm
                            ratingValue={memberOrganizerRating}
                            closeForm={() => setEditRating(false)}
                            setOrganizerRating={submitOrganizerRating}
                            sportTypeId={sportTypeId}
                            data-testid={dataTestId && `${dataTestId}.edit-form`}
                        />
                    ) : (
                        <div className="d-flex flex-column aic">
                            <p className={styles.ratingText} data-testid={dataTestId && `${dataTestId}.rating`}>
                                {hasOrganizerRating && (
                                    <span>
                                        <span
                                            className="organizerRating"
                                        >
                                            {memberOrganizerRating}
                                        </span> ({rating.value}{rating.status === ' Projected' ? ' P' : ''})
                                    </span>
                                )}
                                {!hasOrganizerRating && isTennisSportTypeEvent(sportTypeId) && rating.showPBRRating && (
                                    <PbrRating pbrClass={styles.playerListPbr} pbrRating={rating.pbrRating} />
                                )}
                                {!hasOrganizerRating && isTennisSportTypeEvent(sportTypeId) && !rating.showPBRRating && (
                                    <span> {rating.value} {rating.status === 'Projected' ? getReliability(rating) : ''} </span>
                                )}
                                {!hasOrganizerRating && isPickleBallSportTypeEvent(sportTypeId) && (
                                    <Utr sportTypeId={sportTypeId} player={player} force={{ ratingType }} />
                                )}
                            </p>
                            {showEditRating(rating) && (
                                <p>
                                    <span className={styles.updateText}>
                                        <span className="ml4" onClick={(e) => { clearAll(); e.stopPropagation(); setEditRating(true); }} data-testid={dataTestId && `${dataTestId}.update-btn`}>UPDATE</span>
                                        {showOrganizerRatings && memberOrganizerRating && (
                                            <span
                                                className="ml4"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    await setOrganizerRating(player.memberId, ratingType, '');
                                                }}
                                                data-testid={dataTestId && `${dataTestId}.delete-btn`}
                                            >
                                                <i className="fas fa-trash-alt" />
                                            </span>
                                        )}
                                    </span>
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

PlayerRatingV2.propTypes = {
    player: PropTypes.objectOf(any).isRequired,
    utrType: PropTypes.string.isRequired,
    ratingType: PropTypes.string.isRequired,
    setOrganizerRating: PropTypes.func.isRequired,
    organizerRatings: PropTypes.arrayOf(any),
    showEdit: PropTypes.bool,
    showOrganizerRatings: PropTypes.bool,
    userIsPowered: PropTypes.bool,
    sportTypeId: PropTypes.number,
    'data-testid': PropTypes.string,
};

PlayerRatingV2.defaultProps = {
    showEdit: false,
    showOrganizerRatings: false,
    userIsPowered: false,
    sportTypeId: sportTypeIds.TENNIS,
    organizerRatings: null,
    'data-testid': undefined,
};

export default PlayerRatingV2;
